<template>
  <v-container fluid>
    <PageHeaderSlot showBackButton />

    <v-card v-if="loading">
      <v-skeleton-loader type="image, article" height="600"></v-skeleton-loader>
    </v-card>
    <v-card class="py-4" v-else>
      <v-img v-if="formData.thumbnail" :src="`${$mediaPath}${formData.thumbnail}`" contain max-height="500" />
      <v-card-title tag="h1">{{ formData.title }}</v-card-title>
      <v-card-text>
        <div class="text-caption">{{ $t('publishDate') }}: {{ formData.publish_date }} {{ formData.publish_time }}</div>
        <div class="mt-10 colorBlack--text editor-text" v-html="formData.content"></div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
export default {
  name: 'StudentBlogDetails',
  computed: {
    id() {
      return this.$validate.regexNumber(this.$route.params.id) ? parseInt(this.$route.params.id) : null
    },
  },
  data: () => ({
    loading: true,
    formData: {
      category: null,
      thumbnail: '',
      title: '',
      publish_date: '',
      publish_time: '',
      content: '',
    },
  }),
  methods: {
    async getBlogById() {
      try {
        if (this.$validate.DataValid(this.id)) {
          const data = await this.$Fetcher.GetBlogById(this.id)
          for (const key in this.formData) {
            if (this.$validate.DataValid(data[key])) {
              this.formData[key] = data[key]
            }
          }
          this.loading = false
        } else {
          throw 'noData'
        }
      } catch (err) {
        this.$common.error(err)
        this.showAlert({
          message: 'noData',
          returnLink: 'StudentBlogList',
        })
      } finally {
        this.$store.dispatch('toggleLoadingPage', false)
      }
    },

    async handleSiteLoaded() {
      this.$common.log('>>>>>>>>>>>>>>>>>>>>>>>>>>>> handleSiteLoaded: StudentBlogDetails')
      this.getBlogById()
    },
  },

  destroyed() {
    window.removeEventListener('onSiteLoaded', this.handleSiteLoaded)
  },
  mounted() {
    this.loading = true
    window.addEventListener('onSiteLoaded', this.handleSiteLoaded)
  },
}
</script>

<style lang="scss" scoped>
::v-deep .theme--light.v-sheet .v-card__title {
  color: var(--v-colorBlack-base) !important;
  font-size: 2rem !important;
  font-weight: 700 !important;
}
/* Editor */
::v-deep .editor-text {
  font-size: 16px !important;

  & * {
    line-height: 1.8;
    font-size: 1em;
    font-weight: 400;
  }

  .ql-align-justify {
    text-align: justify;
  }

  .ql-align-center {
    text-align: center;
  }

  .ql-align-right {
    text-align: right;
  }

  & strong,
  & strong * {
    font-weight: 700 !important;
  }

  & p {
    font-size: 1em;
    margin-bottom: 0 !important;
  }

  & h1 {
    font-size: 2em;
    font-weight: 700;
  }

  & h2 {
    font-size: 1.5em;
    font-weight: 500;
  }

  & h3 {
    font-size: 1.17em;
    font-weight: 500;
  }

  & h4 {
    font-size: 1em;
    font-weight: 400;
  }

  & ul,
  & ol {
    list-style: none;
  }

  & ul > li {
    padding-left: 20px;
    position: relative;
  }

  & ul > li::before {
    content: '';
    position: absolute;
    top: 0.7em;
    width: 0.3em;
    height: 0.3em;
    background-color: #3d3f4d;
    border-radius: 100%;
    transform: translateX(-20px);
  }

  & ol {
    counter-reset: list-1 list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9;
  }

  & ol > li {
    position: relative;
    counter-reset: list-1 list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9;
    counter-increment: list-0;
  }

  & ol > li::before {
    content: counter(list-0, decimal) '. ';
    display: inline-block;
    line-height: inherit;
    font-size: inherit;
    color: inherit;
    margin-right: 10px;
  }

  & ol > li.ql-indent-1 {
    counter-reset: list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9;
    counter-increment: list-1;
    padding-left: 1em;
  }

  & ul > li.ql-indent-1 {
    padding-left: 40px;
  }

  & ol > li.ql-indent-1::before {
    content: counter(list-1, lower-alpha) '. ';
  }

  & ol > li.ql-indent-2 {
    counter-reset: list-3 list-4 list-5 list-6 list-7 list-8 list-9;
    counter-increment: list-2;
    padding-left: 2em;
  }

  & ul > li.ql-indent-2 {
    padding-left: 60px;
  }

  & ol > li.ql-indent-2::before {
    content: counter(list-2, lower-roman) '. ';
  }

  & ol > li.ql-indent-3 {
    counter-reset: list-4 list-5 list-6 list-7 list-8 list-9;
    counter-increment: list-3;
    padding-left: 3em;
  }

  & ul > li.ql-indent-3 {
    padding-left: 80px;
  }

  & ol > li.ql-indent-3::before {
    content: counter(list-3, lower-decimal) '. ';
  }

  & ol > li.ql-indent-4 {
    counter-reset: list-5 list-6 list-7 list-8 list-9;
    counter-increment: list-4;
    padding-left: 4em;
  }

  & ul > li.ql-indent-4 {
    padding-left: 100px;
  }

  & ol > li.ql-indent-4::before {
    content: counter(list-4, lower-alpha) '. ';
  }

  & ol > li.ql-indent-5 {
    counter-reset: list-6 list-7 list-8 list-9;
    counter-increment: list-5;
    padding-left: 5em;
  }

  & ul > li.ql-indent-5 {
    padding-left: 120px;
  }

  & ol > li.ql-indent-5::before {
    content: counter(list-5, lower-roman) '. ';
  }

  & ol > li.ql-indent-6 {
    counter-reset: list-7 list-8 list-9;
    counter-increment: list-6;
    padding-left: 6em;
  }

  & ul > li.ql-indent-6 {
    padding-left: 140px;
  }

  & ol > li.ql-indent-6::before {
    content: counter(list-6, lower-decimal) '. ';
  }

  & ol > li.ql-indent-7 {
    counter-reset: list-8 list-9;
    counter-increment: list-7;
    padding-left: 7em;
  }

  & ol > li.ql-indent-7::before {
    content: counter(list-7, lower-alpha) '. ';
  }

  & ul > li.ql-indent-7 {
    padding-left: 160px;
  }

  & ol > li.ql-indent-8 {
    counter-reset: list-9;
    counter-increment: list-8;
    padding-left: 8em;
  }

  & ul > li.ql-indent-8 {
    padding-left: 180px;
  }

  & ol > li.ql-indent-8::before {
    content: counter(list-8, lower-roman) '. ';
  }

  & img {
    height: auto;
    object-fit: contain;
    max-width: 100%;
  }

  & blockquote {
    border-left: 4px solid #ccc;
    margin-bottom: 5px;
    margin-top: 5px;
    padding-left: 1em;
  }

  & pre {
    white-space: pre-wrap;
    background-color: #666;
    margin-bottom: 5px;
    margin-top: 5px;
    padding: 5px 10px;
    color: #f8f8f2;
    overflow: visible;
  }
}
</style>
